import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Trash2, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fabrics`;
const absUrl = (u) => (u?.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/* One click: set the default room style for every color in a collection */
export const DefaultRoomDialog = ({ allColors, token }) => {
  const [open, setOpen] = useState(false);
  const [collection, setCollection] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [room, setRoom] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const collections = [...new Set(allColors.map(c => c.collection_name).filter(Boolean))].sort();

  useEffect(() => { axios.get(`${API}/room-types`).then(({ data }) => setRoomTypes(data)).catch(() => {}); }, []);
  useEffect(() => {
    setCoverage(null); setRoom('');
    if (!collection) return;
    axios.get(`${API}/collections/room-coverage`, { params: { collection_name: collection }, headers })
      .then(({ data }) => setCoverage(data)).catch(() => {});
  }, [collection, headers]);

  const label = (id) => roomTypes.find(r => r.id === id)?.label || id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/collections/default-room`, { collection_name: collection, room_type: room }, { headers });
      toast.success(`${label(room)} is now the default for ${data.updated} colors${data.skipped ? ` (${data.skipped} skipped — no scene)` : ''}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set default room');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" data-testid="default-room-collection-btn">
          <Star className="w-4 h-4" /> Default Room
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Set Default Room for a Collection</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Collection</Label>
            <select className="w-full mt-1 rounded border border-neutral-300 p-2 text-sm" value={collection} onChange={e => setCollection(e.target.value)} data-testid="default-room-collection-select">
              <option value="">Select collection...</option>
              {collections.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {coverage && (
            <div>
              <Label>Room style ({coverage.total} colors)</Label>
              <div className="mt-2 space-y-1.5" data-testid="default-room-options">
                {Object.entries(coverage.rooms).sort((a, b) => b[1] - a[1]).map(([id, n]) => (
                  <label key={id} className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm cursor-pointer ${room === id ? 'border-amber-500 bg-amber-50' : 'border-neutral-200'}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" name="default-room" checked={room === id} onChange={() => setRoom(id)} data-testid={`default-room-option-${id}`} />
                      {label(id)}
                    </span>
                    <span className="text-xs text-neutral-500">{n} / {coverage.total} colors have it</span>
                  </label>
                ))}
                {!Object.keys(coverage.rooms).length && <p className="text-sm text-neutral-500">No room scenes in this collection yet.</p>}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={apply} disabled={!room || saving} data-testid="default-room-apply-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />} Set as Default
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Restore bin: room scenes deleted in the last 7 days */
export const RestoreBinDialog = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    axios.get(`${API}/deleted-scenes`, { headers }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [open, headers]);

  const restore = async (item) => {
    setBusyId(item.id);
    try {
      await axios.post(`${API}/deleted-scenes/${item.id}/restore`, {}, { headers });
      setItems(list => list.filter(i => i.id !== item.id));
      toast.success(`Restored ${item.color_name} · ${item.room_type.replace(/_/g, ' ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setBusyId(null); }
  };

  const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`) - Date.now()) / 86400000));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" data-testid="restore-bin-btn">
          <Trash2 className="w-4 h-4" /> Restore Bin
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Deleted Room Scenes (kept 7 days)</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 py-2" data-testid="restore-bin-list">
          {items === null && <Loader2 className="w-5 h-5 animate-spin mx-auto" />}
          {items?.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">The restore bin is empty.</p>}
          {items?.map(item => (
            <div key={item.id} className="flex items-center gap-3 rounded-lg border border-neutral-200 p-2" data-testid={`restore-bin-item-${item.id}`}>
              <img src={absUrl(item.url)} alt="" className="w-20 h-14 object-cover rounded" loading="lazy" />
              <div className="flex-1 min-w-0 text-sm">
                <p className="font-medium truncate">{item.color_name} · {item.room_type.replace(/_/g, ' ')}</p>
                <p className="text-xs text-neutral-500 truncate">{item.collection_name} · deleted {new Date(item.deleted_at).toLocaleDateString()} · {daysLeft(item.expires_at)} days left</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => restore(item)} disabled={busyId === item.id} data-testid={`restore-bin-restore-${item.id}`}>
                {busyId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />} Restore
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
